import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiMapPin, FiClock, FiSend, FiCheckCircle } = FiIcons;

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const leftRef  = useRef(null);
  const rightRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
      tl.fromTo(leftRef.current,
        { autoAlpha: 0, x: -36 },
        { autoAlpha: 1, x: 0, duration: 0.9 }
      ).fromTo(rightRef.current,
        { autoAlpha: 0, x: 36 },
        { autoAlpha: 1, x: 0, duration: 0.9 },
        '-=0.6'
      );
    });
    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="pt-32 pb-24 relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column: Content */}
          <div ref={leftRef} style={{ opacity: 0 }}>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Let's <span className="text-gradient-cyan">Talk</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed mb-12 max-w-lg">
              Tell us about your project and we'll come back to you within one business day.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-cyan-600 group-hover:border-cyan-300 transition-all">
                  <SafeIcon icon={FiMail} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Email</h4>
                  <p className="text-slate-600">hello@aetrixlogic.com</p>
                </div>
              </div>

              {/* <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-cyan-600 group-hover:border-cyan-300 transition-all">
                  <SafeIcon icon={FiMapPin} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Location</h4>
                  <p className="text-slate-600">India | United Kingdom</p>
                </div>
              </div> */}

              <div className="flex items-start gap-6 group">
                <div className="w-12 h-12 bg-slate-50 border border-slate-200 flex items-center justify-center text-cyan-600 group-hover:border-cyan-300 transition-all">
                  <SafeIcon icon={FiClock} />
                </div>
                <div>
                  <h4 className="text-slate-900 font-bold mb-1">Response Time</h4>
                  <p className="text-slate-600">Within 1 business day</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div ref={rightRef} style={{ opacity: 0 }} className="bg-slate-50 border border-slate-200 p-8 md:p-12 relative shadow-sm">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <SafeIcon icon={FiCheckCircle} className="text-6xl text-cyan-600 mb-6" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Message Received</h3>
                <p className="text-slate-600">Our team will be in touch shortly.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-8 text-cyan-600 font-bold uppercase tracking-widest text-xs hover:text-slate-900 transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Full Name</label>
                    <input required type="text" className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Company</label>
                    <input required type="text" className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Work Email</label>
                  <input required type="email" className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Project Type</label>
                    <select required className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all appearance-none cursor-pointer">
                      <option value="">Select an option</option>
                      <option>Custom Software</option>
                      <option>AI Integration</option>
                      <option>ERP System</option>
                      <option>API Development</option>
                      <option>Cloud Architecture</option>
                      <option>Technology Consulting</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                      Budget
                    </label>

                    <div className="flex gap-2">

                      {/* Currency Selector */}
                      <select className="bg-white border border-slate-200 px-3 text-slate-900 focus:border-cyan-500 outline-none">
                        <option value="USD">$</option>
                        <option value="EUR">€</option>
                        <option value="GBP">£</option>
                        <option value="INR">₹</option>
                      </select>

                      {/* Budget Input */}
                      <input
                        type="text"
                        required
                        placeholder="e.g. 5000 - 20000"
                        className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all"
                      />

                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Message</label>
                  <textarea required rows="4" className="w-full bg-white border border-slate-200 p-4 text-slate-900 focus:border-cyan-500 outline-none transition-all resize-none"></textarea>
                </div>

                <button type="submit" className="w-full py-5 bg-slate-900 text-white font-black uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-3 group shadow-lg shadow-slate-200">
                  Send Message
                  <SafeIcon icon={FiSend} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;